
import React, { useEffect, useRef } from 'react';

interface InitialScrollPositionProps {
  containerRef: React.RefObject<HTMLDivElement>;
  initialScrollPosition: number;
  isInitialPositionReady: boolean;
  content?: string;
}

const InitialScrollPosition: React.FC<InitialScrollPositionProps> = ({
  containerRef,
  initialScrollPosition,
  isInitialPositionReady,
  content
}) => {
  const hasAppliedInitialScroll = useRef(false);
  const contentSignature = useRef('');
  const applyScrollAttempts = useRef(0);
  const maxAttempts = 5;

  // Calculate a signature for the content to detect when it truly changes
  useEffect(() => {
    // Using the first 50 chars as a simple signature
    const newSignature = content?.substring(0, 50) || '';
    if (newSignature !== contentSignature.current) {
      contentSignature.current = newSignature;
      hasAppliedInitialScroll.current = false;
      applyScrollAttempts.current = 0;
      console.log("Content changed, will apply scroll position again");
    }
  }, [content]);

  // Apply initial scroll position after content has rendered and position is ready
  useEffect(() => {
    if (
      containerRef.current && 
      !hasAppliedInitialScroll.current && 
      isInitialPositionReady && 
      initialScrollPosition > 0 &&
      applyScrollAttempts.current < maxAttempts
    ) {
      // Use progressive delays to ensure content is fully rendered
      // First try immediately, then with increasing delays
      const delay = applyScrollAttempts.current === 0 ? 0 : 300 * applyScrollAttempts.current;
      
      const timer = setTimeout(() => {
        if (containerRef.current) {
          console.log(`Applying initial scroll position (attempt ${applyScrollAttempts.current + 1}): ${initialScrollPosition}px for content: "${content?.substring(0, 20)}..."`);
          containerRef.current.scrollTop = initialScrollPosition;
          
          // Check if scroll was actually applied
          const didScrollApply = containerRef.current.scrollTop > 0;
          
          if (didScrollApply) {
            console.log(`Scroll position successfully applied: ${containerRef.current.scrollTop}px`);
            hasAppliedInitialScroll.current = true;
          } else {
            // Try again if content height might not be calculated yet
            applyScrollAttempts.current++;
            console.log(`Scroll not applied yet, will retry. Attempt ${applyScrollAttempts.current} of ${maxAttempts}`);
          }
        }
      }, delay);
      
      return () => clearTimeout(timer);
    }
  }, [initialScrollPosition, content, isInitialPositionReady, containerRef]);

  return null; // This is a behavior-only component
};

export default InitialScrollPosition;
